import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useContentControls, isPageInMenu, isFeatureVisible, isFeatureFlag } from '../hooks/useContentControls';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './ui/sheet';
import {
  Menu,
  Home,
  Search,
  Sun,
  Moon,
  User,
  UserPlus,
  LogIn,
  LogOut,
  PenSquare,
  LayoutDashboard,
  ChevronDown,
  Radio,
  Zap,
  Ticket,
  Headphones,
  CreditCard,
  Share2,
  Facebook,
  MessageCircle,
  Send,
  Copy,
  Bell,
  LibraryBig,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { fetchSiteSettings, fetchCategories } from '../hooks/useSiteData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_SHARE_URL = 'https://infoflow.studio/';
const SITE_SHARE_TITLE = 'InfoFlow';
const SITE_SHARE_TEXT = 'Découvrez InfoFlow, le média intelligent qui transforme l’information en expérience éditoriale premium.';

const buildSiteShareLinks = (url, text) => ({
  whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
});

// Classe commune des liens de la barre de rubriques (scroll horizontal)
const RUBRIQUE_CLASS = 'px-2.5 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wide whitespace-nowrap shrink-0';

const Header = () => {
  const { user, logout, isEditor, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const cc = useContentControls();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breakingNews, setBreakingNews] = useState([]);
  // Tant que la reponse n'est pas arrivee, la hauteur du bandeau est reservee :
  // son apparition tardive decalait toute la page vers le bas.
  const [headerLoaded, setHeaderLoaded] = useState(false);
  const [siteName, setSiteName] = useState('InfoFlow');
  const [activeLive, setActiveLive] = useState(null);
  const shareLinks = buildSiteShareLinks(SITE_SHARE_URL, SITE_SHARE_TEXT);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cats, breakingRes, settings] = await Promise.all([
          fetchCategories(),
          axios.get(`${API_URL}/api/breaking-news`),
          fetchSiteSettings()
        ]);
        setCategories(cats);
        setBreakingNews(breakingRes.data);
        if (settings?.identity?.site_name) {
          setSiteName(settings.identity.site_name);
        }
      } catch (error) {
        console.error('Error fetching header data:', error);
      } finally {
        setHeaderLoaded(true);
      }
    };
    fetchData();

    // Poll for active live streams every 30s
    const fetchLive = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/streaming/active`);
        const lives = res.data?.lives || [];
        setActiveLive(lives.length > 0 ? lives[0] : null);
      } catch { setActiveLive(null); }
    };
    fetchLive();
    // Un onglet en arrière-plan n'a pas de direct à surveiller : le sondage
    // reprend au retour, avec un appel immédiat.
    let liveInterval = setInterval(fetchLive, 30000);
    const onVisibility = () => {
      clearInterval(liveInterval);
      if (!document.hidden) { fetchLive(); liveInterval = setInterval(fetchLive, 30000); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(liveInterval); document.removeEventListener('visibilitychange', onVisibility); };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const copySiteLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(SITE_SHARE_URL);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = SITE_SHARE_URL;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Lien copié');
    } catch {
      toast.error('Partage indisponible');
    }
  };

  const closeShareMenu = () => setShareOpen(false);

  const shareSiteNative = async () => {
    try {
      if (!navigator.share) {
        await copySiteLink();
        return;
      }
      await navigator.share({
        title: SITE_SHARE_TITLE,
        text: SITE_SHARE_TEXT,
        url: SITE_SHARE_URL,
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      await copySiteLink();
      closeShareMenu();
    } finally {
      closeShareMenu();
    }
  };

  const nameHalf = Math.ceil(siteName.length / 2);
  const namePart1 = siteName.slice(0, nameHalf);
  const namePart2 = siteName.slice(nameHalf);

  return (
    <>
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/40">
        {/* Breaking News Ticker — compact (hauteur reservee des le premier rendu) */}
        {(breakingNews.length > 0 || !headerLoaded) && (
          <div className="bg-breaking text-white py-1 overflow-hidden">
            <div className="container mx-auto px-3 sm:px-4 flex items-center gap-2 sm:gap-3 min-h-[18px] sm:min-h-[20px]">
              <span className={`flex items-center gap-1 font-semibold text-[11px] sm:text-xs uppercase tracking-wide whitespace-nowrap${breakingNews.length ? '' : ' invisible'}`}>
                <Zap className="h-3 w-3" />
                Breaking
              </span>
              <div className="overflow-hidden flex-1">
                <div className="ticker-scroll flex gap-12 whitespace-nowrap">
                  {[...breakingNews, ...breakingNews].map((news, i) => (
                    <Link
                      key={i}
                      to={`/article/${news.id}`}
                      className="text-[11px] sm:text-sm hover:underline"
                    >
                      {news.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live Banner */}
        {activeLive && (
          <Link
            to={`/live/${activeLive.id}`}
            className="block bg-[#D62828] text-white py-2 cursor-pointer hover:bg-[#B71C1C] transition-colors"
          >
            <div className="container mx-auto px-4 flex items-center justify-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
              </span>
              <span className="font-bold text-sm uppercase tracking-wide">EN DIRECT</span>
              <span className="text-sm">{activeLive.title}</span>
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Regarder →</span>
            </div>
          </Link>
        )}

        {/* ── Rangée principale : logo + recherche + actions (h-16, contrat top-16 préservé) ── */}
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-3 h-16">
            {/* Menu hamburger (sous lg) */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              {/* La barre ADMIN est fixée en haut (z-100) : sans décalage, elle
                  recouvrait la croix de fermeture du tiroir (portail, z-50). */}
              <SheetContent side="left"
                className={`w-72 overflow-y-auto ${isAdmin ? 'top-10 sm:top-12 h-auto' : ''}`}>
                <nav className="flex flex-col gap-4 mt-8">
                  {/* 1. Accueil */}
                  <SheetClose asChild>
                    <Link to="/" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      <Home className="h-4 w-4" /> Accueil
                    </Link>
                  </SheetClose>
                  {/* 1b. Kiosque — mêmes flags fail-closed que la barre de rubriques et la garde de route */}
                  {(cc?.features?.kiosque?.enabled === true && cc?.features?.kiosque?.public_hall === true) && (
                    <SheetClose asChild>
                      <Link to="/kiosque" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2" data-testid="mobile-nav-kiosque">
                        <LibraryBig className="h-4 w-4 text-[#D62828]" /> Kiosque
                      </Link>
                    </SheetClose>
                  )}
                  {/* 2. Catégories dynamiques (data-driven, inchangé) */}
                  {categories.map((cat) => (
                    <SheetClose asChild key={cat.id}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className="text-lg font-medium hover:text-primary transition-colors"
                      >
                        {cat.name}
                      </Link>
                    </SheetClose>
                  ))}
                  {/* 2b. Rubriques éditoriales statiques (routes dédiées, PR #109) */}
                  <SheetClose asChild>
                    <Link to="/artiste" className="text-lg font-medium hover:text-primary transition-colors">
                      Artiste
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/diaspora" className="text-lg font-medium hover:text-primary transition-colors">
                      Diaspora
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/events" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      <Ticket className="h-4 w-4 text-[#D62828]" /> Événements
                    </Link>
                  </SheetClose>
                  <Separator className="my-2" />
                  {/* 3. En direct */}
                  <SheetClose asChild>
                    <Link to="/live" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      <Radio className="h-4 w-4 text-[#D62828]" /> En direct
                    </Link>
                  </SheetClose>
                  {/* 4. Marchés */}
                  {isPageInMenu(cc, 'markets') && (
                    <SheetClose asChild>
                      <Link to="/marches" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                        <Zap className="h-4 w-4 text-[#D62828]" /> Marchés
                      </Link>
                    </SheetClose>
                  )}
                  {/* 5. Podcasts */}
                  <SheetClose asChild>
                    <Link to="/podcasts" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      <Headphones className="h-4 w-4" /> Podcasts
                    </Link>
                  </SheetClose>
                  {/* 6. Buzz */}
                  <SheetClose asChild>
                    <Link to="/buzz" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      Buzz du net
                    </Link>
                  </SheetClose>
                  {/* 7. Blog */}
                  <SheetClose asChild>
                    <Link to="/blog" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                      Blog
                    </Link>
                  </SheetClose>
                  {/* 8. Abonnement (pricing_visible) */}
                  {isFeatureFlag(cc, 'subscriptions', 'pricing_visible') && (
                    <SheetClose asChild>
                      <Link to="/abonnement" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-[#D62828]" /> Abonnement
                      </Link>
                    </SheetClose>
                  )}
                  {/* 9-10. Compte (déconnecté) : Créer un compte puis Se connecter */}
                  {!user && (
                    <>
                      <Separator className="my-2" />
                      {isFeatureFlag(cc, 'auth', 'register_visible') && (
                        <SheetClose asChild>
                          <Link to="/register" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                            <UserPlus className="h-4 w-4" /> Créer un compte
                          </Link>
                        </SheetClose>
                      )}
                      {/* Se connecter : fallback true, jamais lié au toggle abonnement */}
                      {isFeatureFlag(cc, 'auth', 'login_visible') && (
                        <SheetClose asChild>
                          <Link to="/login" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                            <LogIn className="h-4 w-4" /> Se connecter
                          </Link>
                        </SheetClose>
                      )}
                    </>
                  )}
                  {/* 11-13. Utilitaires : Notifications, Mode sombre, Partager */}
                  <Separator className="my-2" />
                  {isFeatureVisible(cc, 'notifications') && (
                    <SheetClose asChild>
                      <Link to="/profile" className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2">
                        <Bell className="h-4 w-4" /> Notifications
                      </Link>
                    </SheetClose>
                  )}
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2 text-left"
                  >
                    {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                    {theme === 'light' ? 'Mode sombre' : 'Mode clair'}
                  </button>
                  <SheetClose asChild>
                    <button
                      type="button"
                      onClick={shareSiteNative}
                      className="text-lg font-medium hover:text-primary transition-colors flex items-center gap-2 text-left"
                    >
                      <Share2 className="h-4 w-4" /> Partager InfoFlow
                    </button>
                  </SheetClose>
                  {/* 14. À propos — toujours en dernier */}
                  <Separator className="my-2" />
                  <SheetClose asChild>
                    <Link
                      to="/a-propos"
                      className="text-lg font-medium hover:text-primary transition-colors"
                    >
                      À propos
                    </Link>
                  </SheetClose>
                </nav>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
              <span className="text-xl sm:text-2xl font-serif font-bold tracking-tight whitespace-nowrap">
                {namePart1}<span className="text-[#D62828]">{namePart2}</span>
              </span>
            </Link>

            {/* Recherche inline (desktop lg+) */}
            <div className="hidden lg:flex flex-1 justify-center min-w-0">
              <form onSubmit={handleSearch} className="relative w-full max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Rechercher sur InfoFlow..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full pl-9"
                  aria-label="Rechercher"
                  data-testid="search-input"
                />
              </form>
            </div>

            {/* Actions à droite */}
            <div className="flex items-center gap-0.5 sm:gap-1 ml-auto lg:ml-0 shrink-0">
              {/* Recherche — icône (sous lg), ouvre une barre pleine largeur */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSearchOpen((v) => !v)}
                aria-label="Rechercher"
                data-testid="search-btn"
              >
                {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
              </Button>

              {/* Partage — visible lg+ (sinon dans le menu) */}
              <DropdownMenu open={shareOpen} onOpenChange={setShareOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Partager InfoFlow"
                    title="Partager InfoFlow"
                    data-testid="site-share-btn"
                    className="hidden lg:inline-flex"
                  >
                    <Share2 className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="w-64 rounded-xl p-2">
                  <div className="px-2 py-1.5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Partager InfoFlow
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Diffusez la page d’accueil du site.
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={!navigator.share}
                    onSelect={(e) => {
                      e.preventDefault();
                      shareSiteNative();
                    }}
                    className="gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    Partage mobile
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="gap-2">
                    <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer">
                      <MessageCircle className="h-4 w-4 text-green-600" />
                      WhatsApp
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="gap-2">
                    <a href={shareLinks.facebook} target="_blank" rel="noreferrer">
                      <Facebook className="h-4 w-4 text-blue-600" />
                      Facebook
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="gap-2">
                    <a href={shareLinks.telegram} target="_blank" rel="noreferrer">
                      <Send className="h-4 w-4 text-sky-500" />
                      Telegram
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      copySiteLink();
                      setShareOpen(false);
                    }}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copier le lien
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Thème — visible lg+ (sinon dans le menu) */}
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="hidden lg:inline-flex">
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>

              {/* Menu utilisateur / CTA auth */}
              {user ? (
                // modal=false : évite le scroll-lock body de Radix qui, combiné au
                // header sticky, provoquait un scroll-jump au clic sur le trigger.
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Menu utilisateur"
                      data-testid="user-menu-btn"
                    >
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    collisionPadding={12}
                    className="w-56 max-h-[calc(100vh-6rem)] overflow-y-auto"
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="px-2 py-1.5">
                      <div className="text-sm font-medium">{user.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {user.role === 'admin' ? 'Administrateur' : user.role === 'editor' ? 'Rédacteur' : 'Lecteur'}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Mon profil
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/mes-billets" className="flex items-center gap-2" data-testid="menu-my-tickets">
                        <Ticket className="h-4 w-4" />
                        Mes billets
                      </Link>
                    </DropdownMenuItem>
                    {/* Blog : tous les utilisateurs connectés */}

                    <DropdownMenuItem asChild>
                      <Link to="/dashboard/blog" className="flex items-center gap-2">
                        <PenSquare className="h-4 w-4" />
                        Écrire dans le Blog
                      </Link>
                    </DropdownMenuItem>
                    {/* Rédacteur + Admin : création de contenu */}
                    {isEditor && (
                      <DropdownMenuItem asChild>
                        <Link to="/editor" className="flex items-center gap-2">
                          <PenSquare className="h-4 w-4" />
                          Créer un article
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isEditor && (
                      <DropdownMenuItem asChild>
                        <Link to="/editor?format=audio" className="flex items-center gap-2">
                          <Headphones className="h-4 w-4" />
                          Nouveau podcast
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isEditor && (
                      <DropdownMenuItem asChild>
                        <Link to="/my-articles" className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Mes articles
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {/* Admin uniquement : dashboard + agent */}
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Dashboard Admin
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {cc?.features?.events?.enabled === true && cc?.features?.events?.capture_enabled === true && <DropdownMenuItem asChild>
                      <Link to="/organizer" className="flex items-center gap-2"><Ticket className="h-4 w-4" />{user.organizer_workspaces?.some(w => w.workspace_status === 'ACTIVE' && !w.access_revoked) ? 'Espace organisateur' : user.organizer_workspaces?.some(w => w.workspace_status === 'PENDING_APPROVAL') ? 'Espace organisateur — En attente' : user.organizer_workspaces?.length ? 'Espace organisateur — Accès suspendu' : 'Créer mon espace organisateur'}</Link>
                    </DropdownMenuItem>}
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/agent" className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Agent IA
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/video-studio" className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Studio Vidéo
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {(isAdmin || isEditor) && (
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/reporter-studio" className="flex items-center gap-2">
                          <Radio className="h-4 w-4" />
                          Reporter Studio
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-1 sm:gap-1.5">
                  {/* Se connecter — visible sm+ (sur mobile : dans le menu hamburger) */}
                  {isFeatureFlag(cc, 'auth', 'login_visible') && (
                    <Button variant="ghost" asChild className="hidden sm:inline-flex px-2.5 sm:px-3" data-testid="login-btn">
                      <Link to="/login">Se connecter</Link>
                    </Button>
                  )}
                  {/* S'inscrire — CTA principal (≠ abonnement), compact, visible toutes tailles */}
                  {isFeatureFlag(cc, 'auth', 'register_visible') && (
                    <Button asChild className="bg-[#D62828] hover:bg-[#B91C1C] h-9 px-3 whitespace-nowrap" data-testid="signup-btn">
                      <Link to="/register" className="flex items-center gap-1.5">
                        <UserPlus className="h-4 w-4" />
                        S'inscrire
                      </Link>
                    </Button>
                  )}
                </div>
              )}

              {/* En direct — bouton Live (live.visible), visible lg+ (sinon dans le menu). */}
              {isFeatureVisible(cc, 'live') && (
                <Button variant="ghost" size="icon" className="hidden lg:inline-flex text-[#D62828] relative shrink-0" data-testid="live-btn" asChild>
                  <Link to="/live" aria-label="En direct" title="En direct">
                    <Radio className="h-5 w-5" />
                    {activeLive && isFeatureVisible(cc, 'notifications') && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-[#D62828] rounded-full animate-ping" />
                    )}
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Barre de recherche mobile — uniquement quand ouverte (n'augmente pas la hauteur sinon) */}
          {searchOpen && (
            <div className="lg:hidden pb-2">
              <form onSubmit={handleSearch} className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Rechercher sur InfoFlow..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full pl-9"
                  aria-label="Rechercher"
                  autoFocus
                  data-testid="mobile-search-input"
                />
              </form>
            </div>
          )}
        </div>
      </header>

      {/* ── Barre de rubriques — scroll horizontal, toutes tailles (non-sticky : défile avec la page) ── */}
      <nav className="border-b border-border/40 bg-background" aria-label="Rubriques">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center gap-0.5 sm:gap-1 h-11 overflow-x-auto hide-scrollbar">
            <Link to="/" className={RUBRIQUE_CLASS}>Accueil</Link>
            {(cc?.features?.kiosque?.enabled === true && cc?.features?.kiosque?.public_hall === true) && (
              <Link to="/kiosque" className={RUBRIQUE_CLASS} data-testid="nav-kiosque">Kiosque</Link>
            )}
            {categories.map((cat) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} className={RUBRIQUE_CLASS} data-testid={`nav-${cat.slug}`}>
                {cat.name}
              </Link>
            ))}
            <Link to="/artiste" className={RUBRIQUE_CLASS} data-testid="nav-artiste">Artiste</Link>
            <Link to="/diaspora" className={RUBRIQUE_CLASS} data-testid="nav-diaspora">Diaspora</Link>
            <Link to="/events" className={RUBRIQUE_CLASS} data-testid="nav-events">Événements</Link>
            <Link to="/blog" className={RUBRIQUE_CLASS}>Blog</Link>
            <Link to="/buzz" className={RUBRIQUE_CLASS}>Buzz</Link>
            {isPageInMenu(cc, 'markets') && (
              <Link to="/marches" className={RUBRIQUE_CLASS}>Marchés</Link>
            )}
            {isFeatureFlag(cc, 'subscriptions', 'pricing_visible') && (
              <Link to="/abonnement" className={RUBRIQUE_CLASS} data-testid="nav-abonnement">Abonnement</Link>
            )}
            <Link to="/a-propos" className={RUBRIQUE_CLASS}>À propos</Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
